import { useEffect, useState, useCallback } from 'react';
import {
  Card, Table, Tag, Button, Space, Modal, Form, InputNumber, Switch,
  Typography, Tooltip, message
} from 'antd';
import {
  ReloadOutlined,
  StopOutlined,
  CheckCircleOutlined,
  EditOutlined
} from '@ant-design/icons';
import { internalReportDefinitions } from '../../api/internalAdminEndpoints';
import { extractApiError } from '../../utils/format';

// Stage R2: internal-admin report definition catalog.
//
// Read-only by default. The Edit button only mutates the safety
// limits the engine actually enforces (max_pages / max_rows /
// timeout_seconds / async-only / allowed-formats). report_code,
// module_name and template_path are intentionally NOT editable in
// this UI — see services/internalAdmin/reportDefinitions.service.js
// for the rationale.
//
// Permissions: viewing is granted to every staff role; mutating
// requires reports.definitions.manage (super_admin only by default).
// The 403 response from the backend surfaces here as a notification.

export default function ReportDefinitionsPage() {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const out = await internalReportDefinitions.list();
      setRows(Array.isArray(out && out.definitions) ? out.definitions : []);
    } catch (err) {
      message.error(extractApiError(err) || 'Could not load report definitions.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  async function toggleActive(row) {
    try {
      if (row.isActive) await internalReportDefinitions.disable(row.id);
      else              await internalReportDefinitions.enable(row.id);
      message.success(row.isActive ? 'Report disabled.' : 'Report enabled.');
      await refresh();
    } catch (err) {
      message.error(extractApiError(err) || 'Could not change status.');
    }
  }

  function openEdit(row) {
    setEditing(row);
    form.setFieldsValue({
      reportName:    row.reportName,
      description:   row.description || '',
      reportType:    row.reportType,
      maxPages:      row.maxPages,
      maxRows:       row.maxRows,
      timeoutSeconds: row.timeoutSeconds,
      isAsyncOnly:   row.isAsyncOnly
    });
  }

  async function submitEdit() {
    try {
      const body = await form.validateFields();
      await internalReportDefinitions.patch(editing.id, body);
      message.success('Definition updated.');
      setEditing(null);
      await refresh();
    } catch (err) {
      if (err && err.errorFields) return; // form validation
      message.error(extractApiError(err) || 'Could not save changes.');
    }
  }

  const columns = [
    {
      title: 'Active',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 90,
      render: (v) => v
        ? <Tag color="green">active</Tag>
        : <Tag color="default">disabled</Tag>
    },
    {
      title: 'Report',
      dataIndex: 'reportName',
      key: 'reportName',
      render: (v, row) => (
        <div>
          <div style={{ fontWeight: 500 }}>{v}</div>
          <div style={{ color: '#999', fontSize: 12 }}>{row.reportCode}</div>
        </div>
      )
    },
    { title: 'Module',  dataIndex: 'moduleName', key: 'moduleName', width: 130 },
    {
      title: 'Type',
      dataIndex: 'reportType',
      key: 'reportType',
      width: 90,
      render: (v) => <Tag>{v}</Tag>
    },
    {
      title: 'Formats',
      dataIndex: 'allowedFormats',
      key: 'allowedFormats',
      width: 160,
      render: (v) => (Array.isArray(v) ? v : [])
        .map((f) => <Tag key={f}>{f}</Tag>)
    },
    {
      title: 'Max pages / rows / sec',
      key: 'limits',
      width: 200,
      render: (_v, row) =>
        `${row.maxPages} / ${row.maxRows} / ${row.timeoutSeconds}`
    },
    {
      title: 'Template',
      dataIndex: 'templatePath',
      key: 'templatePath',
      ellipsis: true,
      render: (v) => <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</span>
    },
    {
      title: '',
      key: 'actions',
      width: 220,
      render: (_v, row) => (
        <Space size="small">
          <Tooltip title="Edit limits / type / formats">
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(row)}>
              Edit
            </Button>
          </Tooltip>
          {row.isActive
            ? (
              <Tooltip title="Disable: tenants will no longer be able to queue this report">
                <Button size="small" danger icon={<StopOutlined />} onClick={() => toggleActive(row)}>
                  Disable
                </Button>
              </Tooltip>
            )
            : (
              <Tooltip title="Enable: tenants can queue this report">
                <Button size="small" type="primary" icon={<CheckCircleOutlined />} onClick={() => toggleActive(row)}>
                  Enable
                </Button>
              </Tooltip>
            )}
        </Space>
      )
    }
  ];

  return (
    <Card
      title="Report Definitions"
      extra={
        <Button icon={<ReloadOutlined />} onClick={refresh}>Refresh</Button>
      }
    >
      <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
        Catalog of report templates the engine can run. Disable a row to stop
        tenants from queueing it without removing the catalog entry. Editing
        is limited to safety limits and report type — code / module / template
        path are immutable here.
      </Typography.Paragraph>
      <Table
        rowKey="id"
        size="middle"
        loading={loading}
        columns={columns}
        dataSource={rows}
        pagination={{ pageSize: 50, showSizeChanger: false }}
      />
      <Modal
        title={editing && `Edit: ${editing.reportCode}`}
        open={!!editing}
        onCancel={() => setEditing(null)}
        onOk={submitEdit}
        okText="Save"
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item name="reportName" label="Display name" rules={[{ required: true }]}>
            <input className="ant-input" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <textarea className="ant-input" rows={3} />
          </Form.Item>
          <Form.Item name="reportType" label="Report type" rules={[{ required: true }]}
            tooltip="Routes to small / medium / heavy worker queue">
            <select className="ant-input">
              <option value="small">small</option>
              <option value="medium">medium</option>
              <option value="large">large (heavy queue, lower concurrency)</option>
            </select>
          </Form.Item>
          <Form.Item name="maxPages" label="Max pages" rules={[{ required: true }]}>
            <InputNumber min={1} max={10000} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="maxRows"  label="Max rows" rules={[{ required: true }]}>
            <InputNumber min={1} max={5000000} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="timeoutSeconds" label="Timeout (seconds)" rules={[{ required: true }]}>
            <InputNumber min={5} max={3600} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="isAsyncOnly" label="Async only (recommended)" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
